<template>
  <div style="position:absolute;top:0;left:0;right:0;bottom:0;" class="layout-column">
    <div class="layout-column flex"
         style="border-radius:4px;position:relative;"
         v-bind:style="{background: compColor.contentFrame}">

      <ValidationObserver ref="PatientDetailForm" v-slot="{ valid, pristine, reset }" class="layout-column flex">
        <div v-bind:style="{'z-index': '4'}" class="layout-row layout-align-center-center pa-1">
          <div class="layout-row">

            <lx-btn small classes="mr-1"
                    @click="returnToOverview"
                    v-if="patientId"
                    icon="mdi-chevron-left" :tooltip="$t('buttons.back_to_patient_diagnoses.title')" :label="$t('buttons.back_to_patient_diagnoses.value')"
                    hide-label></lx-btn>
            <lx-btn small classes="mr-1" :disabled="pristine"
                    @click="clearForm(reset)"
                    icon="mdi-undo-variant" :tooltip="$t('buttons.revert_patient_changes.title')" :label="$t('buttons.revert_patient_changes.value')"
                    hide-label></lx-btn>
            <lx-btn small classes="mr-1" :disabled="!valid || pristine || !model.insurance_data.insurance_status"
                    @click="savePatientData"
                    icon="save" :tooltip="$t('buttons.save_patient_data.title')" :label="$t('buttons.save_patient_data.value')"
                    hide-label></lx-btn>
          </div>
          <div class="flex layout-row">
            <div v-if="errorMsg && !valid"
                 class="py-4 px-2 body-2 layout-row layout-align-start-center flex-gt-sm-66"
                 :class="{'red darken-5 white--text': isDarkMode, 'red lighten-5 red--text text--darken-3': !isDarkMode}"
                 style="border-radius:4px; height: 40px;"
            >
              <v-icon class="mr-3" :color="isDarkMode ? 'white' : 'red darken-3'">mdi-alert-circle-outline</v-icon>
              <span style="line-height: 100%;">{{ errorMsg }}</span>
            </div>
          </div>
        </div>

        <v-divider v-bind:style="{'border-color':compColor.lightDivider}"></v-divider>

        <div class="flex layout-column pa-1" style="overflow-y: auto;">
          <v-progress-linear :indeterminate="loading" v-if="loading" color="red accent-2"
                             background-color="red lighten-4" class="ma-0"
                             style="border-radius:0;height:4px;position: absolute;top:56px;left:0; width:100%;"></v-progress-linear>

          <lx-expansion-panels :single-open="false" v-model="panel">
            <lx-expansion-panel disabled>
              <lx-expansion-panel-header icon="mdi-account-circle"
                                         disabled
                                         :main-headline="$t('COMPONENT_PATIENT.BASE_DATA_MAIN_LINE')"
                                         :sub-headline="$t('COMPONENT_ACCOUNT.BASE_DATA_SUB_LINE')">
              </lx-expansion-panel-header>
              <lx-expansion-panel-content>
                <div class="pa-4 layout-row layout-wrap">

                  <!-- firstname -->

                  <div class="flex-gt-sm-50 flex-100 px-2">
                    <validation-provider tag="div" ref="firstname" name="firstname"
                                         rules="required|min:2|name_string"
                                         v-slot="{ errors }">
                      <v-text-field
                          :dark="compColor.isInDarkMode"
                          v-model="model.firstname"
                          :label="$t('COMPONENT_PATIENT.FIRSTNAMES') + ' *'"
                          :color="compColor.isInDarkMode ? compColor.primeHighlightColor : compColor.primeColor"
                          counter="200"
                          :dense="denseFields"
                          :hide-details="!errors || errors.length <= 0"
                          :error-messages="errors[0]"
                          outlined
                      ></v-text-field>
                    </validation-provider>
                  </div>

                  <!-- lastname -->

                  <div class="flex-gt-sm-50 flex-100 px-2">
                    <validation-provider tag="div" ref="lastname" name="lastname"
                                         rules="required|min:2|name_string"
                                         v-slot="{ errors }">
                      <v-text-field
                          :dark="compColor.isInDarkMode"
                          v-model="model.lastname"
                          :label="$t('COMPONENT_ACCOUNT.LASTNAME') + ' *'"
                          :color="compColor.isInDarkMode ? compColor.primeHighlightColor : compColor.primeColor"
                          counter="200"
                          :dense="denseFields"
                          :hide-details="!errors || errors.length <= 0"
                          :error-messages="errors[0]"
                          outlined
                      ></v-text-field>
                    </validation-provider>
                  </div>

                  <!-- title -->

                  <div class="flex-gt-sm-50 flex-100 px-2">
                    <validation-provider tag="div" ref="title" name="title"
                                         rules="name_string"
                                         v-slot="{ errors }">
                      <v-combobox
                          :color="compColor.primeColor"
                          :label="$t('COMPONENT_PATIENT.TITLE')"
                          v-model="model.title"
                          :items="titleValues"
                          outlined
                          :hide-details="!errors || errors.length <= 0"
                          :dense="denseFields"
                          :error-messages="errors[0]"
                          :dark="compColor.isInDarkMode">
                        <template v-slot:item="{ item, on, attrs }">
                          <v-list-item v-on="on"
                                       :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                            <v-list-item-content class="py-0">
                              <label style="cursor:pointer;"
                                     :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                     class="lighten-4 text--darken-2">{{ item }}</label>
                            </v-list-item-content>
                          </v-list-item>
                        </template>
                      </v-combobox>
                    </validation-provider>
                  </div>

                  <!-- name_prefix -->

                  <div class="flex-gt-sm-50 flex-100 px-2">
                    <validation-provider tag="div" ref="name_prefix" name="name_prefix"
                                         rules="name_string"
                                         v-slot="{ errors }">
                      <v-combobox
                          :color="compColor.primeColor"
                          v-model="model.name_prefix"
                          outlined
                          :dense="denseFields"
                          :hide-details="!errors || errors.length <= 0"
                          :label="$t('COMPONENT_PATIENT.NAME_PREFIX')"
                          :error-messages="errors[0]"
                          :dark="compColor.isInDarkMode"
                          :items="namePrefixValues">
                        <template v-slot:item="{ item, on, attrs }">
                          <v-list-item v-on="on"
                                       :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                            <v-list-item-content class="py-0">
                              <label style="cursor:pointer;"
                                     :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                     class="lighten-4 text--darken-2">{{ item }}</label>
                            </v-list-item-content>
                          </v-list-item>
                        </template>
                      </v-combobox>
                    </validation-provider>
                  </div>

                  <!-- gender -->

                  <div class="flex-gt-sm-50 flex-100 px-2">
                    <validation-provider tag="div" ref="gender" name="gender"
                                         rules="required"
                                         v-slot="{ errors }">
                      <v-select
                          :color="compColor.primeColor"
                          :label="$t('COMPONENT_PATIENT.GENDER') + ' *'"
                          v-model="model.gender"
                          :items="genderValues"
                          outlined
                          :hide-details="!errors || errors.length <= 0"
                          :dense="denseFields"
                          :error-messages="errors[0]"
                          :menu-props="{ bottom: true, offsetY: true }"
                          :dark="compColor.isInDarkMode">
                        <template v-slot:item="{ item, on, attrs }">
                          <v-list-item v-on="on"
                                       :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                            <v-list-item-content class="py-0">
                              <label style="cursor:pointer;"
                                     :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                     class="lighten-4 text--darken-2">{{ item.text }}</label>
                            </v-list-item-content>
                          </v-list-item>
                        </template>
                      </v-select>
                    </validation-provider>
                  </div>

                  <!-- name_suffix -->

                  <div class="flex-gt-sm-50 flex-100 px-2">
                    <validation-provider tag="div" ref="name_suffix" name="name_suffix"
                                         rules="name_string"
                                         v-slot="{ errors }">
                      <v-combobox
                          :color="compColor.primeColor"
                          v-model="model.name_suffix"
                          outlined
                          :dense="denseFields"
                          :hide-details="!errors || errors.length <= 0"
                          :label="$t('COMPONENT_PATIENT.NAME_SUFFIX')"
                          :error-messages="errors[0]"
                          :dark="compColor.isInDarkMode"
                          :items="nameSuffixValues">
                        <template v-slot:item="{ item, on, attrs }">
                          <v-list-item v-on="on"
                                       :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                            <v-list-item-content class="py-0">
                              <label style="cursor:pointer;"
                                     :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                     class="lighten-4 text--darken-2">{{ item }}</label>
                            </v-list-item-content>
                          </v-list-item>
                        </template>
                      </v-combobox>
                    </validation-provider>
                  </div>

                  <!-- birthday -->

                  <div class="flex-gt-sm-50 flex-100 px-2 mb-4">
                    <validation-provider tag="div"
                                         ref="birthday"
                                         name="birthday"
                                         rules="required|german_date|date_not_in_future"
                                         v-slot="{ errors }">
                      <lx-datepicker
                          :color="compColor.primeColor"
                          :error="errors[0]"
                          :label="$t('COMPONENT_PATIENT.BIRTHDAY') + ' *'"
                          @input="setDateAccuracyAfterDate"
                          :hide-details="!errors || errors.length <= 0"
                          :dense="denseFields"
                          v-model="model.birthday"
                      ></lx-datepicker>
                    </validation-provider>
                  </div>

                  <!-- Datumsgenauigkeit birthday -->

                  <div class="flex-gt-sm-50 flex-100 px-2">
                    <validation-provider tag="div"
                                         name="birthday_accuracy"
                                         ref="birthday_accuracy"
                                         rules="required"
                                         v-slot="{ errors }">
                      <v-select
                          :color="compColor.primeColor"
                          :label="$t('COMPONENT_PATIENT.BIRTHDAY_ACCURACY') + ' *'"
                          v-model="model.birthday_accuracy"
                          :items="DateAccuracyTypes"
                          outlined
                          :hide-details="!errors || errors.length <= 0"
                          :dense="denseFields"
                          :error-messages="errors[0]"
                          :menu-props="{ bottom: true, offsetY: true }"
                          :dark="compColor.isInDarkMode">
                        <template v-slot:item="{ item, on, attrs }">
                          <v-list-item v-on="on"
                                       :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                            <v-list-item-content class="py-0">
                              <label style="cursor:pointer;"
                                     :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                     class="lighten-4 text--darken-2">{{ item.text }}</label>
                            </v-list-item-content>
                          </v-list-item>
                        </template>
                      </v-select>
                    </validation-provider>
                  </div>

                  <!-- ais-id -->

                  <div class="flex-gt-sm-50 flex-100 px-2">
                    <validation-provider tag="div" ref="ais_id" name="ais_id"
                                         :rules="{ required: true, ais_id_must_be_unique: [patientId], trim_string: true }"
                                         v-slot="{ errors }">
                      <v-text-field
                          :dark="compColor.isInDarkMode"
                          v-model="model.ais_id"
                          :label="$t('COMPONENT_PATIENT.AIS_ID') + ' *'"
                          :color="compColor.primeColor"
                          counter="200"
                          :error-messages="errors[0]"
                          placeholder=" "
                          outlined
                          :hide-details="!errors || errors.length <= 0"
                          :dense="denseFields"
                      ></v-text-field>
                    </validation-provider>
                  </div>

                  <!-- ignore in import -->

                  <div class="flex-gt-sm-50 flex-100 px-2">
                    <div class="layout-row flex md12 sm12 xs12 py-1 px-2 report layout-row layout-align-start-center"
                         :class="[!compColor.isInDarkMode ? compColor.prime + ' lighten-5 white--text ' : '']"
                         style="border:1px solid;border-radius: 9px;"
                         :style="{'border-color': compColor.isInDarkMode ? compColor.primeHighlightColor : 'transparent'}">
                      <div class="layout-column layout-align-center-center mr-3">
                        <v-icon size="30" :color="compColor.prime">mdi-information</v-icon>
                      </div>
                      <span style="font-size: 14px;" :class="compColor.primeClass">{{ setLabelForIgnoreForImport() }}</span>

                        <div class="layout-row flex layout-align-end-center">
                          <lx-btn :label="$t('buttons.route_to_blacklist.value')"
                                  width="auto"
                                  :color="compColor.primeColor"
                                  :height="denseFields ? '30' : '40'"
                                  small
                                  classes="text-none ml-3"
                                  @click="routeToBlacklist()">
                          </lx-btn>
                      </div>
                    </div>
                  </div>

                  <!-- former_names -->

                  <div class="flex-gt-sm-50 flex-100 px-2 layout-row">
                    <div class="flex-gt-sm-100 flex-100 pr-1">
                      <validation-provider tag="div" ref="former_names" name="former_names"
                                           rules="name_string"
                                           v-slot="{ errors }">
                        <v-text-field
                            :dark="compColor.isInDarkMode"
                            v-model="model.former_names[0]"
                            :label="$t('COMPONENT_PATIENT.FORMER_NAMES.TITLE')"
                            :color="compColor.primeColor"
                            :error-messages="errors[0]"
                            placeholder=" "
                            outlined
                            :hide-details="!errors || errors.length <= 0"
                            :dense="denseFields"
                        ></v-text-field>
                      </validation-provider>
                    </div>
                  </div>

                  <!-- birth_name -->

                  <div class="flex-gt-sm-50 flex-100 px-2 layout-row">
                    <div class="flex-gt-sm-100 flex-100 pr-1">
                      <validation-provider tag="div" ref="birth_name" name="birth_name"
                                           rules="name_string"
                                           v-slot="{ errors }">
                        <v-text-field
                            :dark="compColor.isInDarkMode"
                            v-model="model.birth_name"
                            :label="$t('COMPONENT_PATIENT.BIRTH_NAME')"
                            :color="compColor.primeColor"
                            :error-messages="errors[0]"
                            placeholder=" "
                            outlined
                            :hide-details="!errors || errors.length <= 0"
                            :dense="denseFields"
                        ></v-text-field>
                      </validation-provider>
                    </div>
                  </div>

                  <!-- patient_informed_consent -->

                  <div class="flex-100 px-2">
                    <fake-input-frame :label="$t('COMPONENT_PATIENT.PATIENT_CONSENT_HEADER')">
                      <validation-provider tag="div" ref="patient_informed_consent" name="patient_informed_consent"
                                           rules="required"
                                           v-slot="{ errors }">

                        <v-radio-group :dense="denseFields" v-model="model.patient_informed_consent" :error-messages="errors[0]" class="pl-3 mb-1 mt-2">
                          <v-radio :color="compColor.primeColor"
                                   :dark="compColor.isInDarkMode"
                                   :label="$t('COMPONENT_PATIENT.PATIENT_CONSENT_YES')"
                                   :value="true"></v-radio>
                          <v-radio :color="compColor.primeColor"
                                   :dark="compColor.isInDarkMode"
                                   :label="$t('COMPONENT_PATIENT.PATIENT_CONSENT_NO')"
                                   :value="false"></v-radio>
                        </v-radio-group>
                      </validation-provider>
                    </fake-input-frame>
                  </div>

                  <!-- Einwilligung nicht meldepflichtig -->

                  <div class="flex-gt-sm-50 flex-100 px-2 layout-row mt-3" v-show="showNotReportableConsent">
                    <label :style="{color: compColor.primeColor}" class="pt-2 mr-3 ml-1">{{ $t('COMPONENT_PATIENT.CONSENT_NOT_REPORTABLE') }} *</label>
                    <validation-provider tag="div"
                                         class="layout-column"
                                         name="einwilligung_nicht_meldepflichtig"
                                         ref="einwilligung_nicht_meldepflichtig"
                                         :rules="{required: showNotReportableConsent}"
                                         v-slot="{ errors }">
                      <v-btn-toggle v-model="model.einwilligung_nicht_meldepflichtig"
                                    dense
                                    class="ml-6"
                                    :mandatory="!!model.einwilligung_nicht_meldepflichtig"
                                    style="margin-top:2px;
                                    background-color: transparent;"
                                    :color="compColor.prime">
                        <v-btn :key="index" v-for="(kind, index) in listJN" :value="kind.value">
                          {{ kind.text }}
                        </v-btn>
                      </v-btn-toggle>
                      <span class="mt-1" style="color:#ff5252; font-size:12px;">{{ errors[0] }}</span>
                    </validation-provider>
                  </div>
                </div>
              </lx-expansion-panel-content>
            </lx-expansion-panel>

            <!-- insurance data -->

            <div class="flex elevation-3 layout-column mb-3" style="border-radius: 5px;"
                 :style="{'background': compColor.lxExpansion.background}">

              <div class="flex-gt-sm-100 layout-row pa-2">
                <div class="layout-align-start-center layout-column">
                  <div style="border-radius:2px;width:40px;height:40px;"
                       :style="{'background': compColor.isInDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}"
                       class="mr-2 layout-column layout-align-center-center">
                    <v-icon :color="compColor.isInDarkMode ? compColor.primeHighlightColor : compColor.primeColor"
                            style="margin:0;">mdi-medical-bag
                    </v-icon>
                  </div>
                </div>
                <div class="layout-column layout-align-center-start blue-grey--text text--darken-2">
                  <div class="subtitle-1 font-weight-light"
                       :class="compColor.isInDarkMode ? compColor.primeHighlightClass : compColor.primeClass"
                       style="lineHeight:15px">
                    <div class="layout-row layout-align-start-center">
                      <span class="mr-3">{{ $t('COMPONENT_PATIENT.INSURANCE_COMPANY_DATA') }}</span>
                    </div>
                  </div>
                  <div class="caption font-weight-light" :class="compColor.widget.subHeadline">
                    {{ $t('COMPONENT_PATIENT.INSURANCE_HEADER_SUB_LINE') }}
                  </div>
                </div>

                <!-- insurance_status -->

                <validation-provider tag="div" name="insurance_data.insurance_status"
                                     rules="required"
                                     class="layout-row flex layout-align-center-center"
                                     v-slot="{}">
                  <span class="body-2 blue-grey--text text--lighten-2 mt-2 ml-5">{{ $t('COMPONENT_PATIENT.INSURANCE_STATE') }} *</span>
                  <v-btn-toggle v-model="model.insurance_data.insurance_status"
                                dense
                                :mandatory="!!model.insurance_data.insurance_status"
                                class="ml-3"
                                style="margin-top:2px;"
                                :color="compColor.prime">
                    <v-btn value="legal" @click="setKindOfInsuranceData('gkv')">
                      {{ $t('COMPONENT_PATIENT.INSURANCE_STATE_LEGAL') }}
                    </v-btn>

                    <v-btn value="private" @click="setKindOfInsuranceData('pkv')">
                      {{ $t('COMPONENT_PATIENT.INSURANCE_STATE_PRIVATE') }}
                    </v-btn>

                    <v-btn value="others" @click="setKindOfInsuranceData('sonstige')">
                      {{ $t('COMPONENT_PATIENT.INSURANCE_STATE_OTHERS') }}
                    </v-btn>
                  </v-btn-toggle>
                </validation-provider>
              </div>

              <v-divider></v-divider>

              <div class="px-4 pt-4 pb-2 layout-row layout-wrap">
                <insurance-data path="insurance_data"
                                class="layout-row layout-wrap flex-gt-sm-100 flex-100"
                                v-model="model.insurance_data"
                ></insurance-data>
              </div>
            </div>

            <!-- address -->

            <lx-expansion-panel disabled :isInvalid="openInvalidModule(2, 'address')">
              <lx-expansion-panel-header icon="mdi-home-variant"
                                         disabled
                                         :main-headline="$t('COMPONENT_PATIENT.ADDRESS')"
                                         :sub-headline="$t('COMPONENT_ADDRESS_DETAILS.HEADER_SUB_LINE')">
              </lx-expansion-panel-header>
              <lx-expansion-panel-content>
                <ValidationObserver tag="div" ref="address">
                  <div class="layout-row flex pt-2 px-3 layout-wrap">

                    <!-- street -->

                    <div class="flex-gt-sm-50 flex-100 pa-2 pb-0">
                      <validation-provider tag="div"
                                           :name="'address.street'"
                                           :ref="'address.street'"
                                           rules="required|min:2|address_string"
                                           v-slot="{ errors }">
                        <v-text-field
                            :dark="compColor.isInDarkMode"
                            :color="compColor.primeColor"
                            :dense="denseFields"
                            :counter="200"
                            outlined
                            :hide-details="!errors || errors.length <= 0"
                            :error-messages="errors[0]"
                            :label="$t('COMPONENT_ADDRESS_DETAILS.STREET') + ' *'"
                            v-model="model.address.street"
                        ></v-text-field>
                      </validation-provider>
                    </div>

                    <!-- number -->

                    <div class="flex-gt-sm-50 flex-100 pa-2 pb-0">
                      <validation-provider tag="div"
                                           :name="'address.number'"
                                           :ref="'address.number'"
                                           rules="address_string"
                                           v-slot="{ errors }">
                        <v-text-field
                            :dark="compColor.isInDarkMode"
                            :color="compColor.primeColor"
                            :dense="denseFields"
                            outlined
                            :hide-details="!errors || errors.length <= 0"
                            :error-messages="errors[0]"
                            :label="$t('COMPONENT_ADDRESS_DETAILS.NUMBER')"
                            v-model="model.address.number"
                        ></v-text-field>
                      </validation-provider>
                    </div>

                    <!-- zip -->

                    <div class="flex-gt-sm-50 flex-100 pa-2 pb-0">
                      <validation-provider tag="div"
                                           :name="'address.zip'"
                                           :ref="'address.zip'"
                                           rules="required|min:2|address_string|zip_string"
                                           v-slot="{ errors }">
                        <v-text-field
                            :dark="compColor.isInDarkMode"
                            :color="compColor.primeColor"
                            :dense="denseFields"
                            outlined
                            :hide-details="!errors || errors.length <= 0"
                            required
                            :error-messages="errors[0]"
                            :label="$t('COMPONENT_ADDRESS_DETAILS.ZIP') + ' *'"
                            v-model="model.address.zip"
                        ></v-text-field>
                      </validation-provider>
                    </div>

                    <!-- city -->

                    <div class="flex-gt-sm-50 flex-100 pa-2">
                      <validation-provider tag="div"
                                           :name="'address.city'"
                                           :ref="'address.city'"
                                           rules="required|min:2|address_string"
                                           v-slot="{ errors }">
                        <v-text-field
                            :dark="compColor.isInDarkMode"
                            :color="compColor.primeColor"
                            :dense="denseFields"
                            outlined
                            :hide-details="!errors || errors.length <= 0"
                            required
                            :error-messages="errors[0]"
                            :label="$t('COMPONENT_ADDRESS_DETAILS.CITY') + ' *'"
                            v-model="model.address.city"
                        ></v-text-field>
                      </validation-provider>
                    </div>

                    <!-- land -->

                    <div class="flex-gt-sm-50 flex-100 px-2">
                      <validation-provider tag="div"
                                           :name="'address.land'"
                                           :ref="'address.land'"
                                           rules=""
                                           v-slot="{ errors }">
                        <v-select
                            :color="compColor.primeColor"
                            :label="$t('COMPONENT_ADDRESS_DETAILS.LAND')"
                            v-model="model.address.land"
                            :items="countryTypes"
                            outlined
                            :hide-details="!errors || errors.length <= 0"
                            :dense="denseFields"
                            :error-messages="errors[0]"
                            :menu-props="{ bottom: true, offsetY: true }"
                            :dark="compColor.isInDarkMode">
                          <template v-slot:item="{ item, on, attrs }">
                            <v-list-item v-on="on"
                                         :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                              <v-list-item-content class="py-0">
                                <label style="cursor:pointer;"
                                       :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                       class="lighten-4 text--darken-2">{{ item.text }}</label>
                              </v-list-item-content>
                            </v-list-item>
                          </template>
                        </v-select>
                      </validation-provider>
                    </div>
                  </div>
                </ValidationObserver>
              </lx-expansion-panel-content>
            </lx-expansion-panel>

            <!-- diagnosis -->

            <lx-expansion-panel v-if="!patientId" disabled>
              <lx-expansion-panel-header icon="mdi-needle"
                                         disabled
                                         :main-headline="$t('COMPONENT_IMPORT.DIAGNOSIS') +  ' *'"
                                         :sub-headline="$t('COMPONENT_PATIENT.DIAGNOSE_HEADER_SUB_LINE')">
              </lx-expansion-panel-header>
              <lx-expansion-panel-content>
                <div class="pa-4 layout-row layout-wrap">
                  <diagnose path="diagnosis"
                            :reportNewDiagnosis="reportNewDiagnosis"
                            @input-ignore="setReportValue"
                            :validationRef="$refs.PatientDetailForm"
                            class="layout-row layout-wrap flex-gt-sm-100 flex-100"
                            v-model="model.diagnosis"></diagnose>
                </div>
              </lx-expansion-panel-content>
            </lx-expansion-panel>
          </lx-expansion-panels>
        </div>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import utils from '../../common/utils'
import metaData from '../../components/documentation/shared/formsMetaData'

import Diagnose from '../Patients/Diagnose'
import InsuranceData from '../Patients/InsuranceData'

export default {
  name: 'PatientDetails',
  mixins: [metaData, utils],
  props: ['patientId', 'tabid'],
  components: {
    Diagnose,
    InsuranceData
  },
  data () {
    return {
      model: {
        address: {},
        diagnosis: {
          diagnosedatum_genauigkeit: 'E'
        },
        insurance_data: {
          insurance_status: 'legal',
          gkv: {}
        },
        patient_informed_consent: true,
        former_names: []
      },
      genderValues: null,
      titleValues: null,
      nameSuffixValues: null,
      namePrefixValues: null,
      countryTypes: null,
      panel: [3, 2, 1, 0],
      loading: false,
      oldModel: {},
      newPatientId: null,
      DateAccuracyTypes: null,
      reportNewDiagnosis: false,
      listJN: [
        { value: true, text: 'Ja' },
        { value: false, text: 'Nein' }
      ],
      showNotReportableConsent: false,
      setIsCompletedIsTriggered: false,
      errorMsg: null
    }
  },
  created () {
    this.genderValues = this.formsMetaData.genderValues
    this.titleValues = this.formsMetaData.titleValues
    this.nameSuffixValues = this.formsMetaData.nameSuffixValues
    this.namePrefixValues = this.formsMetaData.namePrefixValues
    this.countryTypes = this.formsMetaData.countryTypes
    this.DateAccuracyTypes = this.formsMetaData.DateAccuracyLong
  },
  beforeRouteUpdate (to, from, next) {
    /**
     * Notwendig, damit zwei Routen auf die gleiche Komponente gehen, die aber per Tab hin und hergeschaltet werden können und sich nur im Parameter unterscheiden
     * **/
    next()
  },
  methods: {
    /**
     * get name of cancer register to show/hide field "einwilligung_nicht_meldepflichtig"
     *
     * @param isNew Boolean, if true it is a new patient
     */
    getCancerRegisterName (isNew) {
      if (this.userCancerRegisterId) {
        // show ConsentNotReportable when cancer register is 'Niedersachsen'
        this.$http.get('/register/' + this.userCancerRegisterId)
            .then(res => {
              this.showNotReportableConsent = res.data?.name === 'Niedersachsen' || false

              // clone model of new patient after getting cancer register
              if (isNew) {
                if (this.showNotReportableConsent) {
                  this.model.einwilligung_nicht_meldepflichtig = false
                }

                this.oldModel = lodash.cloneDeep(this.model)
              }
            })
            .catch(() => {this.showNotReportableConsent = false})
      } else {
        this.showNotReportableConsent = false
      }
    },

    /**
     * Set text of info-field for ignoring for import
     *
     * @returns {VueI18n.TranslateResult}
     */
    setLabelForIgnoreForImport () {
      let returnValue = this.$t('COMPONENT_PATIENT.IGNORE_TEXT.NO_IGNORE')

      if (this.model.isPermanentIgnored) {
        returnValue = this.$t('COMPONENT_PATIENT.IGNORE_TEXT.PERMANENT_IGNORE')
      }

      if (this.model.isIgnoredUntil && new Date(this.model.isIgnoredUntil).getTime() >= (new Date()).setHours(12,0,0)) {
        returnValue = this.$t('COMPONENT_PATIENT.IGNORE_TEXT.IGNORE_UNTIL', { date: this.$options.filters['formatDate'](this.model.isIgnoredUntil, 'DD.MM.YYYY') })
      }

      return returnValue
    },

    /**
     * Open tab of black list
     *
     */
    routeToBlacklist () {
      this.addTabToStore({routeName: 'Blacklist', options: {icon: 'mdi-ghost', headline: 'Gesperrte Patienten'}})
    },

    /**
     * Set reportNewDiagnosis-value after changing in diagnosis-module
     *
     * @param params {boolean} The changed value
     */
    setReportValue (params) {
      this.reportNewDiagnosis = params
    },

    /**
     * Modify insurance data object in dependency to selected kind of insurance data
     *
     * @param path The selected kind of insurance data
     */
    setKindOfInsuranceData (path) {
      ['gkv', 'pkv', 'sonstige'].forEach(state => {
        // delete all kind of insurance data, if not chosen via button
        // e.g. gkv !== pkv ===> delete gkv
        if (state !== path) {
          this.$delete(this.model.insurance_data, state)
        }

        // set new kind of insurance data, if not exists after selection via button
        // if kind of insurance data already exists, there will be nothing changed
        if (state === path && !this.model.insurance_data[path]) {
          this.$set(this.model.insurance_data, path, {})
        }
      })
    },

    /**
     * Set date accuracy per default after date input
     */
    setDateAccuracyAfterDate () {
      // set birthday_accuracy if not exists after changing birthday in datepicker
      if (this.model.birthday && !this.model.birthday_accuracy) {
        this.model.birthday_accuracy = 'E'
      }
    },

    returnToOverview () {
      this.$emit('input', true)
    },

    /**
     * Wird aufgerufen, wenn man den zurücksetzen Button drückt.
     * - Ursprungsmodel wird wieder an model übergeben
     * - alle Fehler werden gelöscht
     * - alle Felder werden zurückgesetzt, sind also pristine und NICHT dirty
     * */
    clearForm (reset) {
      reset()
      this.model = lodash.cloneDeep(this.oldModel)
      this.setIsCompletedIsTriggered = false
      this.errorMsg = null
    },

    /**
     * wird aufgerufen, wenn man den Speichern-Button drückt.
     *
     * 1 Fall neu anlegen (patientId NICHT vorhanden === Neuanlage):
     *    - per $http.post('/patients') ... Model schicken
     *    - per $http.put('/documentation/forms/register') ... Diagnose schicken
     *    - im Erfolgsfall route zu PatientDetails erneut aufrufen, diesmal mit erhaltener patientId
     * 2 Fall Patient updaten (patientId vorhanden === Update):
     *    - per $http.put('/patients') ... mit patientId und Model wird Patient upgedatet
     *    - anschließend getPatientData ausgeführt
     *
     * */
    savePatientData () {
      if (this.loading) return
      this.loading = true

      delete this.model.id

      if (this.patientId) {
        this.$http.put('/patients/' + this.patientId, this.model)
            .then(res => {
              this.loading = false
              this.errorMsg = null

              this.getPatientData()

              if (this.$refs.PatientDetailForm) {
                this.$refs.PatientDetailForm.reset()
              }

              this.updateTabTitle({
                id: this.tabid,
                headline: this.model.lastname + ', ' + this.model.firstname
              })

              this.NotifySuccess(res, this.$t('COMPONENT_PATIENT.PATIENT_SUCCESSFULLY_SAVED'))
            })
            .catch(error => {
              this.loading = false
              this.populateFormError(this.$refs.PatientDetailForm, error)
                  .catch(this.NotifyError)
            })
      } else {
        const diagnosisBaseData = lodash.cloneDeep(this.model.diagnosis)

        let diagnosis = {
          diagnose: diagnosisBaseData,
          key: 'register'
        }

        // check, if user already exists to prevent duplicate entries in db
        if (!this.newPatientId) {
          // clone model to get possibility to delete diagnosis
          let patient = lodash.cloneDeep(this.model)
          // delete diagnosis from model to prevent validation error of patient in backend
          delete patient.diagnosis

          this.$http.post('/patients', patient)
              .then(res => {
                this.loading = false

                if (res.data && res.data._id) {
                  diagnosis.patient_id = res.data._id
                  // set newPatientId to prevent creating user twice when validation error in register form occurs
                  this.newPatientId = res.data._id
                  // add diagnosis to model to show values in view
                  this.model.diagnosis = diagnosisBaseData

                  this.saveForm(diagnosis, res.data._id)
                }
              })
              .catch((error) => {
                this.loading = false
                this.populateFormError(this.$refs.PatientDetailForm, error).catch(this.NotifyError)
              })
        } else {
          diagnosis.patient_id = this.newPatientId
          this.saveForm(diagnosis, this.newPatientId)
        }
      }
    },

    /**
     * Save new register form in db
     *
     * @param diagnosis The Base data of new register form
     * @param patientId The id of patient to link to
     */
    saveForm (diagnosis, patientId) {
      if (this.loading) return
      this.loading = true

      // add empty histology-element to ensure, that editing in documentation is possible
      diagnosis.histologie = [{}]

      // set report reason to diagnosis
      diagnosis.report_header = {
        meldeanlass: 'diagnose',
        eigenleistung: 'J'
      }

      const self = this

      const ChangeTabHeader = function () {
        self.loading = false

        // remove current tab from store
        if (self.tabid || self.$route?.params?.tabid) {
          self.removeTabFromStoreByTabid(self.tabid || self.$route.params.tabid)
        }

        // add new tab with saved patient and redirect to tab
        self.addTabToStore({
          routeName: 'PatientDetails',
          routeParams: { patientId: patientId },
          options: {
            headline: self.model.lastname + ', ' + self.model.firstname
          }
        })

        self.$nextTick(() => {
          // clear form of new patient
          self.model = {
            address: {},
            diagnosis: {
              diagnosedatum_genauigkeit: 'E'
            },
            insurance_data: {
              insurance_status: 'legal',
              gkv: {}
            },
            patient_informed_consent: true,
            former_names: []
          }
          self.oldModel = lodash.cloneDeep(self.model)
          // reset id of new patient to be able to create several new patients in row
          self.newPatientId = null
          self.reportNewDiagnosis = false
          self.$refs.PatientDetailForm.reset()
        })
      }

      // save new diagnosis form and reload data
      this.$http.put('/documentation/forms/register', diagnosis)
          .then(res => {
            if (res.data && res.data && res.data.form._id) {
              if (!this.reportNewDiagnosis) {
                this.$http.patch('/documentation/formStatus/ignoredForReport/' + res.data.form._id)
                    .then(() => {
                      ChangeTabHeader()
                    })
                    .catch(this.NotifyError)
                    .finally(() => {
                      this.loading = false
                    })
              } else {
                this.reportNewDiagnosis = false
                ChangeTabHeader()
              }
            } else {
              this.loading = false
            }
          })
          .catch((error) => {
            this.loading = false
            this.populateFormError(this.$refs.PatientDetailForm, error)
                .catch(this.NotifyError)
          })
    },

    /**
     * Get existing patient from db
     */
    getPatientData () {
      if (this.loading) return
      this.loading = true
      this.setIsCompletedIsTriggered = false

      this.$http.get('/patients/' + this.patientId) // get patient data
          .then(resultPatient => {
            if (resultPatient.data && resultPatient.data.patient) {
              resultPatient.data.patient.insurance_data = resultPatient.data.patient.insurance_data || {}
              resultPatient.data.patient.address = resultPatient.data.patient.address || {}
              resultPatient.data.patient.former_names = resultPatient.data.patient.former_names || []

              this.model = resultPatient.data.patient
              this.oldModel = lodash.cloneDeep(this.model)

              // copy ais-id from patient to clipboard
              if (this.model.ais_id) {
                const id = this.model.ais_id
                // create textarea with text content to copy to clipboard
                let textArea = document.createElement('textarea')
                textArea.value = id
                document.body.appendChild(textArea)
                textArea.select()
                document.execCommand('copy')
                textArea.remove()
              }

              // validate invalid patient after routing from report
              const storedPatient = this.$store.getters.routingAfterReportValidation(this.patientId)

              if (storedPatient && storedPatient.showBaseData) {
                setTimeout(() => {
                  this.setIsCompletedIsTriggered = true
                  this.errorMsg = this.$t('ERROR_VALIDATION_WHILE_COMPLETING_FORM')

                  this.$refs.PatientDetailForm.validate()
                }, 100)

                // delete stored patient
                this.$store.dispatch('deleteRoutingAfterReportValidation', this.patientId)
              }
            }
          })
          .catch(() => {
            this.removeTabFromStoreByTabid(this.tabid || this.$route?.params?.tabid)
          })
          .finally(() => {
            this.loading = false
          })
    }
  },
  watch: {
    isPristine (newValue, oldValue) {
      if (!newValue && oldValue) {
        this.$nextTick(() => {
          this.$refs.PatientDetailForm?.validate()
          this.updateTab({ id: this.tabid, show: true })
        })
      }
      if (newValue && !oldValue) {
        this.$nextTick(() => {
          this.updateTab({ id: this.tabid, show: false })
        })
      }
    },
    '$route': {
      handler: function (to) {
        if (to?.path === '/patient/details/' + this.tabid + '/' + this.patientId) {
          // refresh data or load initial if switching to patient details
          if (this.patientId) {
            this.getPatientData()
          } else {
            this.oldModel = lodash.cloneDeep(this.model)
          }

          this.getCancerRegisterName(false)
        }

        // get name of cancer register and clone model if new patient
        if (to?.path === '/patient/new/' + this.tabid) {
          this.getCancerRegisterName(true)
        }
      },
      immediate: true
    }
  },
  computed: {
    isPristine () {
      if (!this.oldModel) {
        return false
      }
      return JSON.stringify(this.oldModel) === JSON.stringify(this.model)
    },
    userCancerRegisterId () {
      return this.getUserCancerRegisterDetails().register_id
    }
  }
}
</script>
